/**
 * Moteur de la chasse commune (modes `hunt` et `battle`).
 *
 * Un seul tableau, PLUSIEURS retouches composees dessus. C'est ce qui le
 * distingue de GuessEngine, qui ne connait qu'une zone : ici on voit l'image
 * entiere, et il faut reperer lesquels de ses morceaux ont ete repeints.
 *
 * Une retouche trouvee reste affichee, cerclee d'or, et cesse d'etre cliquable.
 * Le serveur reste seul juge : le clic part chez lui, l'affichage ne fait que
 * suivre les annonces qui reviennent.
 *
 * Comme les autres moteurs, il vit hors de React : aucun setState par frame.
 */
import {
  GUESS_BLIND_MS, decodeDrawing, type DrawZone, type Drawing } from '@retusch/shared';
import { ArtworkRenderer } from './canvas/ArtworkRenderer.js';
import { Compositor } from './canvas/Compositor.js';
import { StrokeCapture } from './canvas/StrokeCapture.js';
import { ZoneCanvas } from './canvas/ZoneCanvas.js';
import { fitViewport, isInsideZone, zoneToRect, type Rect, type Vec2 } from './canvas/geometry.js';
import { ZONE_CANVAS_WIDTH } from './canvas/renderConfig.js';
import { naturalSize } from './artwork.js';

const GOLD = '#e8b84b';
/**
 * Retouche jamais reperee. Vert et non or : c'est une reussite, mais celle de
 * son AUTEUR, pas celle d'un chercheur. Deux couleurs pour deux verdicts
 * opposes, et le trait pointille redit la meme chose sans dependre de la
 * couleur.
 */
const SURVIVED = '#4ade80';
const DANGER = '#f87171';
const NEUTRAL = '#f2f0eb';
/** Duree de l'onde laissee par un clic, en ms. */
const PING_MS = 420;

export interface HuntSlot {
  readonly slot: number;
  readonly drawerId: string;
  readonly zone: DrawZone;
  readonly palette: readonly string[];
  readonly drawing: ArrayBuffer | null;
  /** Ma propre retouche : je la vois, je ne peux pas la trouver. */
  readonly own: boolean;
  /** Pseudo de l'auteur, affiche sur la zone une fois celle-ci decouverte. */
  readonly label: string;
}

export interface HuntEngineOptions {
  readonly canvas: HTMLCanvasElement;
  readonly image: HTMLImageElement;
  readonly slots: readonly HuntSlot[];
  /** Clic sur l'image, en coordonnees normalisees. Le serveur tranche. */
  readonly onStrike: (point: Vec2) => void;
  /**
   * Ai-je encore le droit de chercher ? Faux a la revelation, et faux des que
   * je suis elimine. Relu a CHAQUE clic, pas une seule fois a la construction :
   * on peut etre elimine en pleine chasse, et le tableau reste alors monte.
   * Le pan et le zoom, eux, restent toujours disponibles.
   */
  readonly canStrike?: () => boolean;
  /** Un rate vient de verrouiller la chasse pour ce joueur. */
  readonly onMiss?: () => void;
  /**
   * Un rate ferme-t-il la chasse pour ce joueur ? Vrai partout, SAUF pour le
   * streamer, qui cherche sans limite (cf. `huntGuess` cote serveur).
   *
   * Le verrou local existe pour devancer l'aller-retour reseau : sans lui, deux
   * ou trois clics partent avant que le refus n'arrive. Chez le streamer il n'y
   * a rien a devancer, le serveur ne refuse rien.
   *
   * Une FONCTION, comme `canStrike`, relue a chaque clic : le moteur se
   * construit de facon asynchrone, une valeur figee la serait sur l'etat
   * d'avant le premier instantane.
   */
  readonly lockOnMiss?: () => boolean;
  /**
   * Curseur original / retouche. Reserve a la revelation : pendant la chasse il
   * montrerait l'œuvre d'origine, c'est-a-dire la reponse.
   */
  readonly split?: boolean;
  /** Duree de l'aller-retour automatique du curseur, en ms. */
  readonly autoSlideMs?: number;
}

interface Piece {
  readonly slot: number;
  readonly own: boolean;
  /** Pseudo de l'auteur, peint sous le cadre une fois la retouche trouvee. */
  readonly label: string;
  readonly zone: DrawZone;
  readonly rect: Rect;
  readonly compositor: Compositor;
  readonly zoneCanvas: ZoneCanvas;
}

export class HuntEngine {
  private readonly options: HuntEngineOptions;
  private readonly renderer: ArtworkRenderer;
  private readonly capture: StrokeCapture;
  private readonly pieces: Piece[];
  /** Retouches annoncees comme trouvees : cerclees, et plus cliquables. */
  private found = new Set<number>();
  /** Ondes de clic, purement locales : sans elles, un rate ne fait rien du tout. */
  private pings: Array<{ point: Vec2; at: number; tone: 'hit' | 'miss' | 'neutral' }> = [];
  /**
   * Verrouillage local apres un rate. Le serveur fait autorite, mais l'aller-
   * retour prend le temps qu'il prend : sans ce drapeau, deux ou trois clics de
   * plus partaient avant que le refus n'arrive.
   */
  private locked = false;
  /** Le joueur a place sa vue lui-meme : on ne la lui reprend plus. */
  private viewTouched = false;
  /** Slot unique a peindre, ou null pour toutes (cf. showOnly). */
  private only: number | null = null;
  /**
   * Place de chaque retouche dans la manche (battle royale seulement).
   *
   * Vide partout ailleurs : les autres modes n'ont rien a classer, ils comptent
   * des points. Ici il n'y en a plus, et le rang EST le resultat.
   */
  private placements: ReadonlyMap<number, number> = new Map();
  /** Position du curseur original/retouche, en fraction de la largeur vue. */
  private split = 1;
  private splitTouched = false;
  private startedAt = 0;
  private onSplitChange: ((value: number) => void) | null = null;

  constructor(options: HuntEngineOptions) {
    this.options = options;
    const imageSize = naturalSize(options.image);
    this.renderer = new ArtworkRenderer(options.canvas, imageSize);

    // Un Compositor par retouche. Chacun sait fondre SA zone dans l'œuvre ;
    // seul le premier peint le tableau, les suivants n'ajoutent que leur piece.
    this.pieces = options.slots.map((slot) => {
      const rect = zoneToRect(slot.zone, imageSize);
      const compositor = new Compositor(options.image, imageSize, slot.zone, ZONE_CANVAS_WIDTH);
      const zoneCanvas = new ZoneCanvas(rect.height / rect.width, slot.palette);

      let strokes: Drawing = [];
      if (slot.drawing !== null && slot.drawing.byteLength > 0) {
        try {
          strokes = decodeDrawing(slot.drawing);
        } catch {
          // Blob illisible : la zone reste blanche, ce qui reste jouable.
        }
      }
      zoneCanvas.replay(strokes);
      compositor.invalidateZone();

      return {
        slot: slot.slot,
        own: slot.own,
        label: slot.label,
        zone: slot.zone,
        rect,
        compositor,
        zoneCanvas,
      };
    });

    this.capture = new StrokeCapture(
      {
        element: options.canvas,
        mode: 'inspect',
        toView: (x, y) => this.renderer.clientToView(x, y),
        toZone: (x, y) => this.renderer.clientToNormalized(x, y),
        minPointDistance: () => 1,
        canDraw: () => false,
      },
      {
        onGesture: (delta) => {
          if (delta.dx !== 0 || delta.dy !== 0) {
            this.viewTouched = true;
            this.renderer.panBy(delta.dx, delta.dy);
          }
          if (delta.scale !== 1) {
            this.viewTouched = true;
            this.renderer.zoomAt(delta.scale, delta.center);
          }
        },
        onTap: (view) => {
          if (this.locked || options.canStrike?.() === false) return;
          // Meme regle que dans GuessEngine : la demi-seconde qui suit
          // l'apparition du plateau n'accepte aucun clic. On vient de dessiner,
          // le geste en cours ne doit pas couter la manche.
          if (this.startedAt === 0 || performance.now() - this.startedAt < GUESS_BLIND_MS) return;
          const rect = options.canvas.getBoundingClientRect();
          const point = this.renderer.clientToNormalized(view.x + rect.left, view.y + rect.top);

          // Meme ordre que le serveur : une retouche TROUVABLE l'emporte sur
          // une zone neutre, sinon deux zones qui se touchent seraient jugees
          // differemment de part et d'autre du fil.
          const findable = this.pieces.find(
            (piece) =>
              !piece.own && !this.found.has(piece.slot) && isInsideZone(point, piece.zone),
          );
          const hit = findable !== undefined;
          // Sa propre retouche, ou une deja trouvee : ni trouvaille ni erreur.
          // La premiere ne peut rien donner, la seconde a pu etre prise une
          // fraction de seconde plus tot par quelqu'un d'autre.
          const neutral =
            !hit && this.pieces.some((piece) => isInsideZone(point, piece.zone));

          if (!neutral) {
            // Le rate part au serveur, LUI AUSSI : il a maintenant une
            // consequence (la chasse s'arrete la), donc c'est a l'autorite de
            // la prononcer. Le volume reste borne : au plus une erreur, et
            // autant de trouvailles qu'il y a de retouches.
            options.onStrike(point);
            if (!hit) {
              if (options.lockOnMiss?.() !== false) this.locked = true;
              options.onMiss?.();
            }
          }

          this.pings.push({
            point,
            at: performance.now(),
            tone: neutral ? 'neutral' : hit ? 'hit' : 'miss',
          });
          this.renderer.markDirty();
        },
      },
    );
  }

  /**
   * N'afficher qu'UNE retouche, celle de ce slot.
   *
   * Le mode concours pose toutes les retouches sur la MEME zone : les peindre
   * ensemble les empilerait et on ne verrait que la derniere. On en montre donc
   * une a la fois, celle que le joueur parcourt, sans reconstruire le moteur a
   * chaque changement : le tableau garderait sinon le zoom et le cadrage a
   * chaque fois, et l'image sauterait entre deux comparaisons.
   */
  /**
   * Remplace la retouche d'un slot, SANS reconstruire le moteur.
   *
   * Ecrit pour le mode spectateur, ou le dessin regarde arrive par instantanes
   * toutes les deux secondes. Remonter un moteur a chaque arrivee reprendrait
   * le zoom et le cadrage du joueur deux fois par seconde de trop : c'est la
   * meme regle que `showOnly` et que `HuntBoard`, on ne touche jamais a la vue
   * de quelqu'un qui l'a placee lui-meme.
   */
  setDrawing(slot: number, drawing: ArrayBuffer | null, palette: readonly string[]): void {
    const piece = this.pieces.find((candidate) => candidate.slot === slot);
    if (piece === undefined) return;

    let strokes: Drawing = [];
    if (drawing !== null && drawing.byteLength > 0) {
      try {
        strokes = decodeDrawing(drawing);
      } catch {
        // Blob illisible : on garde la zone vide plutot que de casser l'ecran.
      }
    }
    piece.zoneCanvas.setPalette(palette);
    piece.zoneCanvas.replay(strokes);
    piece.compositor.invalidateZone();
    this.renderer.markDirty();
  }

  /**
   * Place de chaque retouche, en battle royale : slot vers rang, 1 pour la
   * meilleure.
   *
   * Le rang decide de la COULEUR du cadre autant que du chiffre : rouge pour le
   * dernier (trouve en premier, donc elimine), vert pour le premier, or entre
   * les deux. Un joueur doit pouvoir lire son sort sans compter les cadres.
   *
   * Comme `setFound`, cette methode ne reconstruit rien : les places changent a
   * chaque decouverte, et remonter le moteur reprendrait la vue de tout le
   * monde en pleine chasse.
   */
  setPlacements(places: ReadonlyMap<number, number>): void {
    this.placements = places;
    this.renderer.markDirty();
  }

  showOnly(slot: number | null): void {
    this.only = slot;
    this.renderer.markDirty();
  }

  /** Retouches annoncees trouvees par le serveur. */
  setFound(slots: readonly number[]): void {
    this.found = new Set(slots);
    this.renderer.markDirty();
  }

  /** Verrouille la chasse pour ce joueur (rate prononce par le serveur). */
  lock(): void {
    this.locked = true;
  }

  /** Fraction 0-1 correspondant a une abscisse en px CSS. */
  splitFromView(x: number): number {
    return Math.min(Math.max(x / Math.max(this.renderer.view.width, 1), 0), 1);
  }

  /** Abscisse du curseur en px CSS, pour positionner la poignee DOM. */
  get splitPx(): number {
    return this.split * this.renderer.view.width;
  }

  /** Deplacement par la poignee. Fige l'aller-retour automatique. */
  setSplit(value: number): void {
    this.splitTouched = true;
    this.split = Math.min(Math.max(value, 0), 1);
    this.renderer.markDirty();
  }

  onSplit(listener: (value: number) => void): void {
    this.onSplitChange = listener;
  }

  start(): void {
    this.startedAt = performance.now();
    this.capture.attach();
    this.renderer.start(this.render);
    this.renderer.setViewport(fitViewport(this.renderer.image, this.renderer.view), false);
  }

  resize(): void {
    this.renderer.resize();
    // Tant que le joueur n'a pas place sa vue, on recadre sur le tableau
    // entier : c'est le service rendu quand la boite change. Des qu'il a
    // touche a quelque chose, en revanche, ce recadrage lui arrachait des yeux
    // la zone qu'il etait en train d'examiner, au pire moment d'une chasse.
    if (!this.viewTouched) {
      this.renderer.setViewport(fitViewport(this.renderer.image, this.renderer.view), false);
      return;
    }
    this.renderer.setViewport(this.renderer.getViewport());
  }

  destroy(): void {
    this.capture.detach();
    this.renderer.stop();
  }

  private readonly render = (
    ctx: CanvasRenderingContext2D,
    viewport: { scale: number; tx: number; ty: number },
    now: number,
  ): void => {
    const first = this.pieces[0];
    if (first === undefined) return;
    const view = this.renderer.view;

    // Aller-retour automatique du curseur a l'ouverture de la revelation, le
    // temps de montrer ce qu'il fait. Il s'arrete des qu'on y touche.
    const slide = this.options.autoSlideMs ?? 0;
    if (this.options.split === true && !this.splitTouched && now - this.startedAt < slide) {
      const t = (now - this.startedAt) / slide;
      this.split = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
      // Le renderer ne repeint que sur invalidation : tant que le curseur bouge
      // tout seul, c'est a nous de redemander la frame suivante.
      this.renderer.markDirty();
    }
    if (this.options.split === true) this.onSplitChange?.(this.split);

    // Le tableau, une seule fois. `zone: 'artwork'` ne pose aucun calque :
    // l'œuvre d'origine sert de fond, les retouches viennent par-dessus.
    first.compositor.draw(ctx, viewport, null, { grain: false, feather: true, zone: 'artwork' });

    // Hors revelation, le curseur est a fond a droite : on ne montre jamais
    // l'œuvre d'origine pendant la chasse, ce serait donner la reponse.
    const splitX = this.options.split === true ? view.width * this.split : view.width;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, splitX, view.height);
    ctx.clip();

    // Chaque piece pose SA zone, sans repeindre le fond : un `draw` complet par
    // piece effacerait les precedentes.
    for (const piece of this.pieces) {
      if (this.only !== null && piece.slot !== this.only) continue;
      piece.compositor.drawZoneOn(ctx, viewport, piece.zoneCanvas.canvas, true);
    }

    // Cadres, dans le meme clip : a droite du curseur il n'y a plus de
    // retouche, un cadre y designerait du vide.
    //
    // Pendant la CHASSE, seules les trouvees sont encadrees : montrer les
    // autres donnerait la reponse. A la REVELATION, tout se montre, y compris
    // celles qui ont tenu : c'est le moment ou l'on decouvre ou chacun s'etait
    // cache, et une retouche jamais reperee est le meilleur coup de la manche.
    const reveal = this.options.split === true;
    for (const piece of this.pieces) {
      if (this.only !== null && piece.slot !== this.only) continue;
      const found = this.found.has(piece.slot);
      if (!found && !reveal) continue;

      const x = piece.rect.x * viewport.scale + viewport.tx;
      const y = piece.rect.y * viewport.scale + viewport.ty;
      const width = piece.rect.width * viewport.scale;
      const height = piece.rect.height * viewport.scale;
      // Battle royale : la place tient lieu de verdict, et sa couleur le dit.
      // Le dernier (trouve en premier) sort de la partie, le premier a tenu.
      const rank = this.placements.get(piece.slot);
      const color =
        rank === undefined
          ? found
            ? GOLD
            : SURVIVED
          : rank === this.pieces.length
            ? DANGER
            : rank === 1
              ? SURVIVED
              : GOLD;
      // Le rang precede le pseudo : c'est le chiffre qu'on cherche des yeux.
      const label = rank === undefined ? piece.label : `${rank} · ${piece.label}`;

      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.shadowColor = color;
      ctx.shadowBlur = 14;
      // Pointille pour ce qui n'a pas ete vu : le verdict se lit meme sans
      // distinguer les couleurs.
      if (!found) ctx.setLineDash([7, 5]);
      ctx.strokeRect(x, y, width, height);
      ctx.restore();

      // Le nom de l'auteur, sous le cadre : une cachette doit dire a qui elle
      // etait, sinon la manche se resume a des rectangles anonymes.
      this.drawLabel(ctx, label, x + width / 2, y + height, view, color, found);
    }
    ctx.restore();

    // Grain unique, sur le composite entier : c'est lui qui fait tenir les
    // pieces ensemble. L'appliquer par piece les trahirait par leur texture.
    first.compositor.drawGrainOn(ctx, viewport);

    if (this.options.split === true) {
      ctx.save();
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 2;
      ctx.shadowColor = GOLD;
      ctx.shadowBlur = 16;
      ctx.beginPath();
      ctx.moveTo(splitX, 0);
      ctx.lineTo(splitX, view.height);
      ctx.stroke();
      ctx.restore();
    }

    this.drawPings(ctx, viewport, now);
  };

  /**
   * Ondes de clic. Le rendu ne tourne que quand quelque chose a change : tant
   * qu'une onde vit, on redemande explicitement une frame.
   */
  /**
   * Etiquette d'auteur. Taille FIXE en pixels d'ecran : rapportee au viewport,
   * elle deviendrait illisible de loin et gigantesque de pres. Elle se replie
   * au-dessus du cadre quand il n'y a plus de place en dessous.
   */
  private drawLabel(
    ctx: CanvasRenderingContext2D,
    label: string,
    centerX: number,
    bottomY: number,
    view: { width: number; height: number },
    color: string,
    found: boolean,
  ): void {
    if (label === '') return;

    ctx.save();
    ctx.font = '600 13px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    const glyph = 16;
    const paddingX = 9;
    const height = 22;
    const textWidth = ctx.measureText(label).width;
    const width = Math.min(textWidth + glyph + paddingX * 2, view.width - 16);
    const below = bottomY + 6 + height / 2;
    const y = below + height / 2 > view.height ? bottomY - height : below;
    const x = Math.min(Math.max(centerX, width / 2 + 8), view.width - width / 2 - 8);
    const left = x - width / 2;

    // Pastille sombre : le pseudo doit rester lisible sur n'importe quel
    // tableau, et l'or seul ne suffit pas sur un aplat clair.
    ctx.fillStyle = 'rgba(16, 16, 20, 0.82)';
    ctx.beginPath();
    ctx.roundRect(left, y - height / 2, width, height, height / 2);
    ctx.fill();

    ctx.strokeStyle = color;
    this.drawVerdictGlyph(ctx, left + paddingX + 6, y, found);

    ctx.fillStyle = color;
    ctx.fillText(label, left + paddingX + glyph, y + 0.5);
    ctx.restore();
  }

  /**
   * Le verdict, en une icone : une coche pour la retouche tombee, un œil barre
   * pour celle que personne n'a vue. La couleur seule ne suffirait pas, et un
   * mot de plus n'entrerait pas dans la pastille.
   */
  private drawVerdictGlyph(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    found: boolean,
  ): void {
    ctx.save();
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (found) {
      ctx.beginPath();
      ctx.moveTo(x - 4, y);
      ctx.lineTo(x - 1, y + 3.2);
      ctx.lineTo(x + 4.5, y - 3.6);
      ctx.stroke();
      ctx.restore();
      return;
    }

    ctx.beginPath();
    ctx.moveTo(x - 5.5, y);
    ctx.quadraticCurveTo(x, y - 4.6, x + 5.5, y);
    ctx.quadraticCurveTo(x, y + 4.6, x - 5.5, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x, y, 1.4, 0, Math.PI * 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x - 5, y + 4.6);
    ctx.lineTo(x + 5, y - 4.6);
    ctx.stroke();
    ctx.restore();
  }

  private drawPings(
    ctx: CanvasRenderingContext2D,
    viewport: { scale: number; tx: number; ty: number },
    now: number,
  ): void {
    if (this.pings.length === 0) return;
    this.pings = this.pings.filter((ping) => now - ping.at < PING_MS);

    for (const ping of this.pings) {
      const t = (now - ping.at) / PING_MS;
      const size = this.renderer.image;
      ctx.save();
      ctx.globalAlpha = 1 - t;
      ctx.strokeStyle =
        ping.tone === 'hit' ? GOLD : ping.tone === 'miss' ? DANGER : NEUTRAL;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(
        ping.point.x * size.width * viewport.scale + viewport.tx,
        ping.point.y * size.height * viewport.scale + viewport.ty,
        6 + t * 22,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
      ctx.restore();
    }

    if (this.pings.length > 0) this.renderer.markDirty();
  }
}
